// An English-friendly "sounds like" spelling built from the phrase's IPA, with the
// stressed syllable in capitals: «Θα ήθελα έναν καφέ» → "tha EE-theh-la EH-nan ka-FEH".
// Conventions follow common phrasebooks: dh as in "this", th as in "thin", gh a soft
// throaty g, h as in Scottish "loch".

const VOWELS = new Set(['a', 'e', 'i', 'o', 'u']);

/** Clusters easy for English speakers to start a syllable with. */
const EASY_ONSETS = new Set([
  'pr', 'tr', 'kr', 'br', 'dr', 'ɡr', 'fr', 'vr', 'θr', 'ðr', 'xr', 'ɣr',
  'pl', 'kl', 'bl', 'ɡl', 'fl', 'vl', 'xl', 'ɣl',
  'sp', 'st', 'sk', 'sm', 'sn', 'zm', 'zv', 'sf', 'sx',
  'str', 'spr', 'skr', 'spl', 'skl',
]);

// Multi-character IPA symbols first, so they are read as one sound.
const SOUNDS: Array<[string, string]> = [
  ['ts', 'ts'],
  ['dz', 'dz'],
  ['θ', 'th'],
  ['ð', 'dh'],
  ['ɣ', 'gh'],
  ['x', 'h'],
  ['ç', 'h'],
  ['ʝ', 'y'],
  ['ɲ', 'ny'],
  ['ŋ', 'n'],
  ['ɡ', 'g'],
  ['g', 'g'],
  ['c', 'ky'],
  ['ɟ', 'gy'],
  ['ʎ', 'ly'],
  ['ɱ', 'm'],
  ['a', 'a'],
  ['e', 'e'],
  ['i', 'ee'],
  ['o', 'o'],
  ['u', 'oo'],
];

/** Splits an IPA word into sound symbols, keeping the stress mark as its own token. */
function tokens(word: string): string[] {
  const out: string[] = [];
  let i = 0;
  while (i < word.length) {
    const two = word.slice(i, i + 2);
    if (two === 'ts' || two === 'dz') {
      out.push(two);
      i += 2;
    } else {
      out.push(word[i]);
      i += 1;
    }
  }
  return out;
}

function letters(sound: string, prev: string | undefined, next: string | undefined): string {
  if (sound === 'ç' && prev && !VOWELS.has(prev)) return 'y';
  if (sound === 'ɲ' && prev === 'm') return 'y';
  // A syllable-final x is easier to read as "kh" (καληνύχτα → ka-lee-NEEKH-ta).
  if (sound === 'x' && next === undefined) return 'kh';
  return SOUNDS.find(([ipa]) => ipa === sound)?.[1] ?? sound;
}

/**
 * One IPA word → syllables in English-friendly spelling, stressed ones upper case. A word with an
 * enclitic can carry two stresses (το διαβατήριό σας).
 */
export function respellWord(ipaWord: string): string {
  const raw = tokens(ipaWord.replace(/[ˌ.]/g, ''));
  // Mark which sounds start a stressed syllable, then drop the marks.
  const sounds: string[] = [];
  const stressMarks: number[] = [];
  for (const t of raw) {
    if (t === 'ˈ') stressMarks.push(sounds.length);
    else sounds.push(t);
  }
  const nuclei = sounds.map((s, i) => (VOWELS.has(s) ? i : -1)).filter((i) => i >= 0);
  if (nuclei.length === 0) return sounds.map((s, i) => letters(s, sounds[i - 1], sounds[i + 1])).join('');

  // Syllable starts: the stress mark wins; otherwise a lone consonant joins the next vowel,
  // an easy cluster stays together, anything else splits after its first consonant.
  const starts = new Set<number>([0]);
  for (let n = 1; n < nuclei.length; n++) {
    const prevNucleus = nuclei[n - 1];
    const nucleus = nuclei[n];
    const clusterStart = prevNucleus + 1;
    const cluster = sounds.slice(clusterStart, nucleus);
    let start: number;
    const mark = stressMarks.find((at) => at > prevNucleus && at <= nucleus);
    if (mark !== undefined) start = mark;
    else if (cluster.length <= 1) start = clusterStart;
    else if (EASY_ONSETS.has(cluster.join(''))) start = clusterStart;
    else if (cluster.length >= 3 && EASY_ONSETS.has(cluster.slice(1).join(''))) start = clusterStart + 1;
    else start = nucleus - 1;
    starts.add(start);
  }
  // A stressed syllable is the one holding the first vowel at or after a stress mark.
  const stressedNuclei = stressMarks.map((at) => nuclei.find((i) => i >= at)).filter((i): i is number => i !== undefined);
  const ordered = [...starts].sort((a, b) => a - b);
  return ordered
    .map((s, k) => {
      const end = ordered[k + 1] ?? sounds.length;
      const syllable = sounds.slice(s, end);
      let text = syllable.map((snd, j) => letters(snd, sounds[s + j - 1], syllable[j + 1])).join('');
      // An open "e" reads as English "ee" or "uh" (he, the), so it is written "eh"; a one-syllable
      // word ending in "o" reads as "too" (to), so it is written "oh".
      const last = syllable[syllable.length - 1];
      if (last === 'e') text += 'h';
      else if (last === 'o' && ordered.length === 1 && syllable.length > 1) text += 'h';
      const stressed = stressedNuclei.some((n) => n >= s && n < end);
      return stressed ? text.toUpperCase() : text;
    })
    .join('-');
}

/** A whole IPA phrase → "tha EE-theh-la EH-nan ka-FEH". */
export function respell(ipa: string): string {
  return ipa
    .trim()
    .split(/\s+/)
    .map(respellWord)
    .join(' ');
}

/** The letter pairs a respelling uses that need explaining, as short keys. */
export function respellKey(respelled: string): string[] {
  const lower = respelled.toLowerCase();
  const keys: string[] = [];
  if (lower.includes('dh')) keys.push('dh as in "this"');
  if (/(^|[^d])th/.test(lower)) keys.push('th as in "thin"');
  if (lower.includes('gh')) keys.push('gh a soft, throaty g');
  if (/kh|(^|[^tdgeo])h/.test(lower.replace(/eh|oh/g, ''))) keys.push('h/kh as in "loch"');
  return keys;
}
